import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, ArrowRight, Percent } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Cube3D } from "../components/Cube3D";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api } from "../lib/api";

export default function QuotePage() {
  const [q, setQ] = useState(null);
  const [service, setService] = useState("Stampa 3D");
  const [material, setMaterial] = useState("");
  const [quality, setQuality] = useState("");
  const [urgency, setUrgency] = useState("");
  const [laserMat, setLaserMat] = useState("");
  const [weight, setWeight] = useState(50);
  const [minutes, setMinutes] = useState(15);
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [checkingLoyalty, setCheckingLoyalty] = useState(false);
  const [code, setCode] = useState("");
  const [codeInfo, setCodeInfo] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const quote = r.data.quote;
      setQ(quote);
      setMaterial(quote.materials?.[0]?.name || "");
      setQuality(quote.qualities?.[1]?.name || quote.qualities?.[0]?.name || "");
      setUrgency(quote.urgency?.[0]?.name || "");
      setLaserMat(quote.laser?.materials?.[0]?.name || "");
    });
  }, []);

  useEffect(() => {
    const e = email.trim();
    if (!e || !e.includes("@")) { setLoyalty(null); return; }
    const t = setTimeout(async () => {
      setCheckingLoyalty(true);
      try { const r = await api.get("/loyalty", { params: { email: e } }); setLoyalty(r.data); }
      catch { setLoyalty(null); } finally { setCheckingLoyalty(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [email]);

  const currency = q?.currency || "€";
  let total = 0;
  if (q) {
    const urg = q.urgency?.find((u) => u.name === urgency)?.multiplier || 1;
    if (service === "Stampa 3D") {
      const m = q.materials?.find((x) => x.name === material)?.price_per_gram || 0;
      const ql = q.qualities?.find((x) => x.name === quality)?.multiplier || 1;
      total = ((Number(weight) || 0) * m * ql * urg + (q.setup_fee || 0)) * (Number(qty) || 1);
      total = Math.max(total, q.min_price || 0);
    } else {
      const L = q.laser || {};
      const mm = L.materials?.find((x) => x.name === laserMat)?.multiplier || 1;
      total = ((Number(minutes) || 0) * (L.price_per_minute || 0) * mm * urg + (L.setup_fee || 0)) * (Number(qty) || 1);
      total = Math.max(total, L.min_price || 0);
    }
  }

  const discount = codeInfo?.valid ? codeInfo.percent : (loyalty?.eligible ? (loyalty.discount_percent || 10) : 0);
  const finalTotal = total * (1 - discount / 100);

  const checkLoyalty = async () => {
    if (!email.trim()) return;
    setCheckingLoyalty(true);
    try {
      const r = await api.get("/loyalty", { params: { email: email.trim() } });
      setLoyalty(r.data);
    } catch { setLoyalty(null); } finally { setCheckingLoyalty(false); }
  };

  const fld = "bg-input border-white/10 text-white rounded-sm";

  const applyCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setCodeInfo(null); return; }
    try { const r = await api.get(`/discount/${c}`); setCodeInfo(r.data); toast.success(`Buono valido: -${r.data.percent}%`); }
    catch { setCodeInfo(null); toast.error("Buono sconto non valido"); }
  };

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 relative">
        <Cube3D className="hidden md:block absolute top-10 right-2 opacity-60" size={84} variant="green" />
        <span className="font-mono text-xs uppercase tracking-widest text-neon-cyan">Stima online</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white mt-2 mb-8">
          Preventivo <span className="text-neon-green drop-shadow-[0_0_14px_rgba(57,255,20,0.5)]">rapido</span>
        </h1>

        {!q ? (
          <p className="font-mono text-neon-cyan animate-pulse-glow">Caricamento…</p>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 bg-card border border-white/10 rounded-sm p-6 sm:p-8 space-y-6">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Servizio</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="quote-service" className={fld}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10 text-white">
                    <SelectItem value="Stampa 3D">Stampa 3D</SelectItem>
                    <SelectItem value="Incisione Laser">Incisione Laser</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {service === "Stampa 3D" ? (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Materiale</Label>
                      <Select value={material} onValueChange={setMaterial}>
                        <SelectTrigger data-testid="quote-material" className={fld}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-white/10 text-white">
                          {q.materials?.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Qualità</Label>
                      <Select value={quality} onValueChange={setQuality}>
                        <SelectTrigger data-testid="quote-quality" className={fld}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-white/10 text-white">
                          {q.qualities?.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Peso stimato (grammi)</Label>
                    <Input data-testid="quote-weight" type="number" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} className={fld} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Materiale da incidere</Label>
                    <Select value={laserMat} onValueChange={setLaserMat}>
                      <SelectTrigger data-testid="quote-laser-material" className={fld}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-white/10 text-white">
                        {q.laser?.materials?.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Tempo di incisione stimato (minuti)</Label>
                    <Input data-testid="quote-minutes" type="number" min="0" value={minutes} onChange={(e) => setMinutes(e.target.value)} className={fld} />
                  </div>
                </>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Urgenza</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger data-testid="quote-urgency" className={fld}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 text-white">
                      {q.urgency?.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Quantità</Label>
                  <Input data-testid="quote-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className={fld} />
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">La tua email <span className="text-neon-green">(per lo sconto fedeltà)</span></Label>
                <Input data-testid="quote-email" type="email" placeholder="nome@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={fld} />
                {checkingLoyalty && <p className="text-xs text-muted-foreground mt-1 font-mono animate-pulse">Verifica fedeltà…</p>}
                {loyalty && !loyalty.eligible && loyalty.count > 0 && (
                  <p data-testid="quote-loyalty-progress" className="text-xs text-muted-foreground mt-1 font-light">Hai {loyalty.count}/{loyalty.threshold} ordini — ancora {loyalty.remaining} per lo sconto del {loyalty.discount_percent}%.</p>
                )}
                {loyalty?.eligible && (
                  <p data-testid="quote-loyalty-active" className="text-xs text-neon-green mt-1 font-light">Cliente fedele: sconto {loyalty.discount_percent}% applicato!</p>
                )}
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Hai un buono sconto?</Label>
                <Input data-testid="quote-code" placeholder="Es. FEDELE-XXXXX" value={code} onChange={(e) => setCode(e.target.value)} onBlur={applyCode} className={fld} />
                {codeInfo?.valid && <p data-testid="quote-code-valid" className="text-xs text-neon-green mt-1 font-light">Buono {codeInfo.percent}% applicato!</p>}
              </div>
            </div>

            <div className="lg:col-span-2">
              <motion.div layout className="sticky top-24 bg-card border border-neon-green/40 rounded-sm p-8 text-center" style={{ boxShadow: "0 0 24px rgba(57,255,20,0.15)" }}>
                <Calculator className="w-8 h-8 text-neon-green mx-auto mb-4" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Stima indicativa</p>
                {discount > 0 && (
                  <p className="text-base text-muted-foreground line-through">{currency}{total.toFixed(2)}</p>
                )}
                <div data-testid="quote-total" className="font-display font-black text-5xl text-neon-green mt-1 drop-shadow-[0_0_16px_rgba(57,255,20,0.5)]">
                  {currency}{finalTotal.toFixed(2)}
                </div>
                {discount > 0 && (
                  <span data-testid="quote-discount-badge" className="inline-block mt-2 px-3 py-1 text-xs font-mono rounded-sm bg-neon-green/15 text-neon-green border border-neon-green/40">Sconto fedeltà -{discount}%</span>
                )}
                {q.note && <p className="text-xs text-muted-foreground font-light mt-4">{q.note}</p>}
                <Link to={codeInfo?.valid ? `/richiesta?code=${code.trim().toUpperCase()}` : "/richiesta"}>
                  <Button data-testid="quote-to-request" className="w-full mt-6 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 transition-colors duration-200">
                    Invia richiesta <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
